// src/components/auth/TermsAndConditionsModal.tsx

import React from "react";

interface TermsAndConditionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept?: () => void;
  onReject?: () => void;
  isBlocking?: boolean;
  isLoading?: boolean;
}

/**
 * Terms and Conditions Modal Component
 * Displays the full terms and conditions for Hermes Terminal platform
 * Responsive design that works across all screen sizes
 */
export const TermsAndConditionsModal: React.FC<
  TermsAndConditionsModalProps
> = ({ isOpen, onClose, onAccept, onReject, isBlocking = false, isLoading = false }) => {
  const handleAccept = () => {
    if (onAccept) {
      onAccept();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4 animate-fadeIn">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={isBlocking || isLoading ? undefined : onClose}
      />

      {/* Modal Container - Responsive sizing */}
      <div className="relative w-full max-w-[95vw] sm:max-w-3xl lg:max-w-4xl xl:max-w-5xl max-h-[90vh] bg-gray-800/95 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-2xl border border-purple-500/20 flex flex-col animate-slideUp">
        {/* Header - Sticky at top */}
        <div className="sticky top-0 z-10 bg-gray-800/95 backdrop-blur-xl border-b border-gray-700 px-4 sm:px-6 py-4 sm:py-5 rounded-t-xl sm:rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">
                Terms and Conditions
              </h2>
              <p className="text-gray-400 text-xs sm:text-sm mt-1">
                Effective Date: November 17, 2025
              </p>
            </div>
            {!isBlocking && !isLoading && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors p-1"
                aria-label="Close terms and conditions">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="prose prose-invert prose-sm sm:prose-base max-w-none">
            {/* Introduction */}
            <section className="mb-6 sm:mb-8">
              <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                Welcome to Hermes Terminal. These Terms and Conditions
                ("Terms", "Agreement") govern your access to and use of the
                Hermes Terminal platform, website, and services (collectively,
                the "Service"). By accessing or using the Service, you agree to
                be bound by these Terms. If you do not agree to these Terms, do
                not use the Service.
              </p>
            </section>

            {/* Section 1: Acceptance of Terms */}
            <section className="mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                1. Acceptance of Terms
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  1.1. By registering for, accessing, or using the Service, you
                  acknowledge that you have read, understood, and agree to be
                  bound by these Terms and our Privacy Policy.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  1.2. You represent and warrant that you are at least 18 years
                  of age and have the legal capacity to enter into this
                  Agreement.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  1.3. We reserve the right to modify these Terms at any time.
                  Continued use of the Service after changes constitutes
                  acceptance of the modified Terms.
                </p>
              </div>
            </section>

            {/* Section 2: Access and Account Registration */}
            <section className="mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                2. Access and Account Registration
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  2.1. Access to the Service is by invitation only. You must
                  submit an access request and receive approval from Hermes
                  Terminal before using the Service.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  2.2. You agree to provide accurate, current, and complete
                  information during the registration process and to update such
                  information to keep it accurate, current, and complete.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  2.3. You are responsible for maintaining the confidentiality
                  of your account credentials and for all activities that occur
                  under your account.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  2.4. You must immediately notify Hermes Terminal of any
                  unauthorized use of your account or any other breach of
                  security.
                </p>
              </div>
            </section>

            {/* Section 3: Data Usage and Privacy */}
            <section className="mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                3. Data Usage and Privacy
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  3.1. Hermes Terminal collects and processes personal data in
                  accordance with our Privacy Policy. By using the Service, you
                  consent to such collection and processing.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  3.2. <strong className="text-white">Data Protection:</strong>{" "}
                  Hermes Terminal will not sell, rent, or share your personal
                  data with third parties without your express consent, except
                  as required by law or as necessary to provide the Service.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  3.3. You retain all rights to any data you input into the
                  Service. Hermes Terminal may use aggregated, anonymized data
                  for analytics and service improvement purposes.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  3.4. <strong className="text-white">Security Measures:</strong>{" "}
                  Hermes Terminal implements industry-standard security
                  measures to protect your data, including encryption,
                  secure authentication, and regular security audits. However,
                  no method of transmission over the Internet or electronic
                  storage is 100% secure.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  3.5. <strong className="text-white">Security Limitations:</strong>{" "}
                  While we employ reasonable security measures, we cannot
                  guarantee absolute security. Unauthorized access, hardware or
                  software failures, and other factors beyond our control may
                  compromise the security of user data. You acknowledge that
                  you provide your information at your own risk.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  3.6. <strong className="text-white">Third-Party Security Risks:</strong>{" "}
                  The Service relies on third-party infrastructure providers
                  (including but not limited to cloud hosting services, content
                  delivery networks, and data centers). While we select reputable
                  providers, we are not responsible for security breaches,
                  data loss, or unauthorized access that occur due to
                  vulnerabilities or failures in third-party systems beyond
                  our reasonable control.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  3.7. <strong className="text-white">Breach Notification:</strong>{" "}
                  In the event of a data breach that affects your personal
                  information, we will make reasonable efforts to notify you
                  in accordance with applicable laws and regulations.
                </p>
              </div>
            </section>

            {/* Section 4: Service Availability and Third-Party Dependencies */}
            <section className="mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                4. Service Availability and Third-Party Dependencies
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  4.1. <strong className="text-white">No Guarantee of Availability:</strong>{" "}
                  While we strive to provide reliable and uninterrupted service,
                  Hermes Terminal does not guarantee 100% uptime or availability.
                  The Service may be unavailable due to maintenance, updates,
                  technical issues, or circumstances beyond our control.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  4.2. <strong className="text-white">Third-Party Dependencies:</strong>{" "}
                  The Service relies on various third-party providers and
                  infrastructure, including but not limited to:
                </p>
                <ul className="list-disc pl-5 sm:pl-6 space-y-2 text-gray-300 text-sm sm:text-base">
                  <li>
                    Cloud infrastructure providers (e.g., Amazon Web Services,
                    Google Cloud Platform, Microsoft Azure);
                  </li>
                  <li>
                    Content delivery networks (e.g., Cloudflare, Akamai);
                  </li>
                  <li>
                    Payment processors and financial service providers;
                  </li>
                  <li>
                    Data providers and API services for cryptocurrency market
                    information;
                  </li>
                  <li>
                    Authentication and identity verification services;
                  </li>
                  <li>
                    Email and communication service providers.
                  </li>
                </ul>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  4.3. <strong className="text-white">Third-Party Outages:</strong>{" "}
                  Service interruptions, slowdowns, or complete outages may
                  occur due to failures or issues with third-party providers.
                  Hermes Terminal is not liable for any damages, losses, or
                  inconveniences resulting from third-party service disruptions,
                  including but not limited to AWS outages, Cloudflare issues,
                  DNS failures, or internet backbone problems.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  4.4. <strong className="text-white">Maintenance and Updates:</strong>{" "}
                  We reserve the right to perform scheduled or emergency
                  maintenance that may temporarily interrupt access to the
                  Service. Where reasonably possible, we will provide advance
                  notice of scheduled maintenance.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  4.5. <strong className="text-white">Data Accuracy:</strong>{" "}
                  While we make reasonable efforts to ensure the accuracy of
                  data provided through the Service, we rely on third-party
                  data sources for cryptocurrency prices, market information,
                  and analytics. We do not guarantee the accuracy, completeness,
                  or timeliness of any data displayed in the Service.
                </p>
              </div>
            </section>

            {/* Section 5: Communications and Marketing */}
            <section className="mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                5. Communications and Marketing
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  5.1. <strong className="text-white">Newsletter and Notifications:</strong>{" "}
                  By accepting these Terms and Conditions and creating an account,
                  you consent to receiving periodic communications from Hermes
                  Terminal, including but not limited to:
                </p>
                <ul className="list-disc pl-5 sm:pl-6 space-y-2 text-gray-300 text-sm sm:text-base">
                  <li>
                    Product updates, new features, and service announcements;
                  </li>
                  <li>
                    Educational content, market insights, and crypto analytics
                    reports;
                  </li>
                  <li>
                    Security alerts and important account notifications;
                  </li>
                  <li>
                    Newsletter subscriptions featuring industry trends and
                    platform highlights;
                  </li>
                  <li>
                    Marketing communications about Hermes Terminal services and
                    offerings.
                  </li>
                </ul>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  5.2. <strong className="text-white">Opt-Out Rights:</strong>{" "}
                  You may opt out of receiving marketing communications and
                  newsletters at any time by:
                </p>
                <ul className="list-disc pl-5 sm:pl-6 space-y-2 text-gray-300 text-sm sm:text-base">
                  <li>
                    Accessing the User Profile section within your account
                    settings and updating your communication preferences;
                  </li>
                  <li>
                    Clicking the "unsubscribe" link included at the bottom of
                    any marketing email;
                  </li>
                  <li>
                    Contacting our support team directly with your opt-out
                    request.
                  </li>
                </ul>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  5.3. <strong className="text-white">Essential Communications:</strong>{" "}
                  Please note that certain communications are essential to your
                  use of the Service and cannot be opted out of, including:
                  security alerts, account verification emails, service
                  disruption notices, changes to these Terms, and responses to
                  your support inquiries.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  5.4. <strong className="text-white">Communication Methods:</strong>{" "}
                  Hermes Terminal may contact you via email, in-app
                  notifications, push notifications (if enabled), or other
                  communication channels you have provided or authorized.
                </p>
              </div>
            </section>

            {/* Section 6: Acceptable Use Policy */}
            <section className="mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                6. Acceptable Use Policy
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  6.1. <strong className="text-white">Prohibited Activities:</strong>{" "}
                  You agree not to engage in any of the following prohibited
                  activities:
                </p>
                <ul className="list-disc pl-5 sm:pl-6 space-y-2 text-gray-300 text-sm sm:text-base">
                  <li>
                    Attempting to gain unauthorized access to any portion of the
                    Service, other accounts, computer systems, or networks
                    connected to the Service through hacking, password mining,
                    or any other means;
                  </li>
                  <li>
                    Engaging in any form of automated data collection, including
                    but not limited to web scraping, data mining, or harvesting,
                    without explicit written permission from Hermes Terminal;
                  </li>
                  <li>
                    Launching or facilitating any form of denial-of-service
                    (DoS) or distributed denial-of-service (DDoS) attacks
                    against the Service or any other party;
                  </li>
                  <li>
                    Transmitting any viruses, malware, worms, trojan horses, or
                    other malicious code that could harm the Service or its
                    users;
                  </li>
                  <li>
                    Using the Service for any illegal purpose or in violation of
                    any local, state, national, or international law;
                  </li>
                  <li>
                    Attempting to interfere with, compromise, or disrupt the
                    integrity or performance of the Service;
                  </li>
                  <li>
                    Creating multiple accounts for the purpose of abusing the
                    Service or circumventing access restrictions;
                  </li>
                  <li>
                    Sending spam, unsolicited communications, or engaging in any
                    form of harassment of other users;
                  </li>
                  <li>
                    Impersonating any person or entity, or falsely stating or
                    otherwise misrepresenting your affiliation with a person or
                    entity;
                  </li>
                  <li>
                    Attempting to reverse engineer, decompile, disassemble, or
                    otherwise attempt to discover the source code of the
                    Service.
                  </li>
                </ul>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  6.2. Hermes Terminal reserves the right to investigate
                  suspected violations of these Terms and to take appropriate
                  action, including but not limited to reporting suspected
                  unlawful activity to law enforcement officials.
                </p>
              </div>
            </section>

            {/* Section 7: Service Restrictions and Enforcement */}
            <section className="mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                7. Service Restrictions and Enforcement
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  7.1. <strong className="text-white">Right to Restrict:</strong>{" "}
                  Hermes Terminal reserves the right, at its sole discretion, to
                  restrict, suspend, or terminate your access to the Service at
                  any time, with or without notice, for any reason, including
                  but not limited to:
                </p>
                <ul className="list-disc pl-5 sm:pl-6 space-y-2 text-gray-300 text-sm sm:text-base">
                  <li>Violation of these Terms or any applicable law;</li>
                  <li>
                    Detection of suspicious, fraudulent, or malicious activity;
                  </li>
                  <li>
                    Attempted or actual unauthorized access to systems or data;
                  </li>
                  <li>
                    Abuse of the Service, including excessive API calls or
                    resource consumption;
                  </li>
                  <li>
                    Failure to pay any fees owed to Hermes Terminal (if
                    applicable);
                  </li>
                  <li>Any other conduct that we deem inappropriate or harmful.</li>
                </ul>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  7.2. <strong className="text-white">Security Monitoring:</strong>{" "}
                  Hermes Terminal employs automated systems to detect and
                  prevent abuse of the Service. These systems may identify
                  patterns indicative of DDoS attacks, unauthorized data
                  scraping, or other malicious activities.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  7.3. <strong className="text-white">Permanent Blocks:</strong>{" "}
                  Users found to be engaging in severe violations, including but
                  not limited to hacking attempts, DDoS attacks, or large-scale
                  data theft, may be permanently blocked from accessing the
                  Service, with no possibility of reinstatement.
                </p>
              </div>
            </section>

            {/* Section 8: Legal Remedies */}
            <section className="mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                8. Legal Remedies and Liability
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  8.1. <strong className="text-white">Right to Legal Action:</strong>{" "}
                  Hermes Terminal reserves the right to pursue any and all legal
                  remedies available under applicable law in response to
                  violations of these Terms, including but not limited to:
                </p>
                <ul className="list-disc pl-5 sm:pl-6 space-y-2 text-gray-300 text-sm sm:text-base">
                  <li>
                    Seeking injunctive relief to prevent further violations;
                  </li>
                  <li>
                    Pursuing civil claims for damages, including compensatory
                    and punitive damages;
                  </li>
                  <li>
                    Reporting criminal activity to law enforcement authorities;
                  </li>
                  <li>
                    Recovering all costs associated with investigating and
                    prosecuting violations, including attorney fees.
                  </li>
                </ul>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  8.2. <strong className="text-white">Indemnification:</strong>{" "}
                  You agree to indemnify, defend, and hold harmless Hermes
                  Terminal, its affiliates, officers, directors, employees, and
                  agents from and against any and all claims, damages,
                  obligations, losses, liabilities, costs, and expenses
                  (including attorney fees) arising from:
                </p>
                <ul className="list-disc pl-5 sm:pl-6 space-y-2 text-gray-300 text-sm sm:text-base">
                  <li>Your use of the Service;</li>
                  <li>Your violation of these Terms;</li>
                  <li>
                    Your violation of any third-party right, including any
                    intellectual property, privacy, or other proprietary right.
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 9: Intellectual Property */}
            <section className="mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                9. Intellectual Property Rights
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  9.1. The Service and its entire contents, features, and
                  functionality (including but not limited to all information,
                  software, text, displays, images, video, and audio, and the
                  design, selection, and arrangement thereof) are owned by
                  Hermes Terminal and are protected by United States and
                  international copyright, trademark, patent, trade secret, and
                  other intellectual property or proprietary rights laws.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  9.2. You are granted a limited, non-exclusive,
                  non-transferable license to access and use the Service for
                  your personal or internal business purposes, subject to these
                  Terms.
                </p>
              </div>
            </section>

            {/* Section 10: Disclaimer of Warranties */}
            <section className="mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                10. Disclaimer of Warranties
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  10.1. THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE"
                  BASIS, WITHOUT WARRANTIES OF ANY KIND. HERMES TERMINAL MAKES
                  NO REPRESENTATIONS OR WARRANTIES OF ANY KIND, EXPRESS OR
                  IMPLIED, AS TO THE OPERATION OF THE SERVICE OR THE
                  INFORMATION, CONTENT, OR MATERIALS INCLUDED THEREIN.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  10.2. TO THE FULLEST EXTENT PERMITTED BY LAW, HERMES TERMINAL
                  DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT
                  NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
                  FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  10.3. HERMES TERMINAL DOES NOT WARRANT THAT THE SERVICE WILL
                  BE UNINTERRUPTED, SECURE, OR ERROR-FREE, OR THAT DEFECTS WILL
                  BE CORRECTED. WE DO NOT GUARANTEE THE ACCURACY, RELIABILITY,
                  COMPLETENESS, OR TIMELINESS OF ANY INFORMATION, DATA, OR
                  CONTENT PROVIDED THROUGH THE SERVICE.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  10.4. <strong className="text-white">Not Financial Advice:</strong>{" "}
                  THE SERVICE IS NOT FINANCIAL ADVICE. All information
                  provided through the Service is for informational purposes
                  only and should not be construed as investment, financial,
                  legal, or tax advice. Any trading or investment decisions you
                  make based on information from the Service are made at your
                  own risk.
                </p>
              </div>
            </section>

            {/* Section 11: Limitation of Liability */}
            <section className="mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                11. Limitation of Liability
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  11.1. TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO
                  EVENT SHALL HERMES TERMINAL, ITS AFFILIATES, OFFICERS,
                  DIRECTORS, EMPLOYEES, AGENTS, OR SUPPLIERS BE LIABLE FOR ANY
                  INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
                  DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED
                  DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL,
                  OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
                </p>
                <ul className="list-disc pl-5 sm:pl-6 space-y-2 text-gray-300 text-sm sm:text-base">
                  <li>Your use or inability to use the Service;</li>
                  <li>
                    Any unauthorized access to or use of our servers and/or any
                    personal information stored therein;
                  </li>
                  <li>
                    Any interruption or cessation of transmission to or from
                    the Service;
                  </li>
                  <li>
                    Any bugs, viruses, trojan horses, or the like that may be
                    transmitted to or through the Service by any third party;
                  </li>
                  <li>
                    Any errors or omissions in any content or for any loss or
                    damage incurred as a result of the use of any content
                    posted, emailed, transmitted, or otherwise made available
                    through the Service;
                  </li>
                  <li>
                    Third-party service failures, including but not limited to
                    AWS, Cloudflare, or other infrastructure provider outages;
                  </li>
                  <li>
                    Any investment or trading losses resulting from your use of
                    the Service.
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 12: Force Majeure */}
            <section className="mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                12. Force Majeure
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  12.1. Hermes Terminal shall not be liable for any delay or
                  failure to perform resulting from causes outside our reasonable
                  control, including but not limited to:
                </p>
                <ul className="list-disc pl-5 sm:pl-6 space-y-2 text-gray-300 text-sm sm:text-base">
                  <li>
                    Acts of God, natural disasters, epidemics, or pandemics;
                  </li>
                  <li>War, terrorism, riots, or civil unrest;</li>
                  <li>
                    Government actions, laws, regulations, or restrictions;
                  </li>
                  <li>
                    Internet or telecommunications service provider failures;
                  </li>
                  <li>
                    Third-party cloud infrastructure or hosting failures (AWS,
                    Google Cloud, Azure, etc.);
                  </li>
                  <li>
                    Content delivery network failures (Cloudflare, Akamai,
                    etc.);
                  </li>
                  <li>Cyber attacks, including DDoS attacks;</li>
                  <li>Power outages or equipment failures;</li>
                  <li>
                    Labor disputes, strikes, or work stoppages affecting our
                    operations or third-party service providers.
                  </li>
                </ul>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  12.2. In the event of a force majeure situation, Hermes
                  Terminal's obligations under these Terms shall be suspended
                  for the duration of such event, and we will make reasonable
                  efforts to restore service as soon as practicable.
                </p>
              </div>
            </section>

            {/* Section 13: Governing Law */}
            <section className="mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                13. Governing Law and Dispute Resolution
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  13.1. These Terms shall be governed by and construed in
                  accordance with the laws of the United States, without regard
                  to its conflict of law provisions.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  13.2. Any legal action or proceeding arising under these Terms
                  will be brought exclusively in the federal or state courts
                  located in the United States, and you hereby irrevocably
                  consent to the personal jurisdiction and venue therein.
                </p>
              </div>
            </section>

            {/* Section 14: Severability */}
            <section className="mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                14. Severability
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  14.1. If any provision of these Terms is found to be invalid
                  or unenforceable, the remaining provisions will remain in full
                  force and effect.
                </p>
              </div>
            </section>

            {/* Section 15: Contact Information */}
            <section className="mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                15. Contact Information
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  15.1. If you have any questions about these Terms, please
                  contact us through the support channels provided within the
                  Service.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  15.2. Hermes Terminal may provide notices to you via email,
                  regular mail, or postings on the Service.
                </p>
              </div>
            </section>

            {/* Section 16: Beta Confidentiality and Non-Disclosure Requirements */}
            <section className="mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                16. Beta Confidentiality and Non-Disclosure Requirements
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  16.1. <strong className="text-white">Confidential Information:</strong>{" "}
                  You acknowledge that during your access to the beta version of
                  the Service ("Beta"), you may be exposed to confidential and
                  proprietary information, including but not limited to: product
                  designs, features, workflows, algorithms, interfaces,
                  performance data, concepts, branding, roadmap materials, and
                  any non-public information related to Hermes Terminal
                  (collectively, "Confidential Information").
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  16.2. <strong className="text-white">Non-Disclosure:</strong>{" "}
                  You agree not to disclose, publish, distribute, transmit, or
                  make available any Confidential Information to any third party
                  without prior written consent from Hermes Terminal.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  16.3. <strong className="text-white">No Redistribution:</strong>{" "}
                  You may not share your access credentials, provide
                  demonstrations, distribute screenshots, videos, summaries, or
                  otherwise reproduce any aspect of the Service.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  16.4. <strong className="text-white">No Public Discussion:</strong>{" "}
                  You may not discuss any part of the Beta publicly, including
                  online forums, social media, blog posts, research materials,
                  presentations, or reviews, without explicit written permission.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  16.5. <strong className="text-white">Use of Information:</strong>{" "}
                  You agree to use Confidential Information solely for the
                  purpose of accessing and evaluating the Service as intended,
                  and for no other purpose.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  16.6. <strong className="text-white">Indirect Disclosure:</strong>{" "}
                  You may not disclose Confidential Information indirectly
                  through advice, guidance, strategic input, consulting, or any
                  other means, even if you do not share the actual materials or
                  access to the Service.
                </p>
              </div>
            </section>

            {/* Section 17: No Competitive Use and Restrictions on Feature Copying */}
            <section className="mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                17. No Competitive Use and Restrictions on Feature Copying
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  17.1. <strong className="text-white">Competitive Restrictions:</strong>{" "}
                  You agree not to use the Service or any Confidential
                  Information to create, modify, improve, operate, market, or
                  distribute any product or service that competes with,
                  replicates, or performs substantially similar functions to
                  Hermes Terminal.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  17.2. <strong className="text-white">No Copying of Concepts or Workflows:</strong>{" "}
                  You agree not to copy, imitate, or replicate any features,
                  ideas, workflows, user experiences, interface layouts,
                  algorithms, or underlying concepts revealed during your use of
                  the Beta.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  17.3. <strong className="text-white">No Benchmarking:</strong>{" "}
                  You may not perform, publish, or disclose any benchmark tests,
                  performance evaluations, or competitive analyses related to
                  the Service.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  17.4. <strong className="text-white">Reverse Engineering for Competitive Intent:</strong>{" "}
                  Attempting to analyze, dissect, or study the Service for the
                  purpose of building competing products is strictly prohibited.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  17.5. <strong className="text-white">Assistance to Third Parties:</strong>{" "}
                  You may not assist, advise, consult, contribute to, or
                  otherwise support any third party in developing, designing,
                  planning, enhancing, or operating any product or service that
                  competes with or imitates Hermes Terminal, whether directly or
                  indirectly, whether compensated or not.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  17.6. <strong className="text-white">Use of Knowledge:</strong>{" "}
                  You may not use any concepts, ideas, workflows, insights, or
                  knowledge gained from the Service to benefit any third party
                  or competing effort.
                </p>
              </div>
            </section>

            {/* Section 18: Feedback Ownership and Intellectual Property Assignment */}
            <section className="mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                18. Feedback Ownership and Intellectual Property Assignment
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  18.1. <strong className="text-white">Feedback Assignment:</strong>{" "}
                  Any suggestions, ideas, proposals, concepts, feature
                  recommendations, improvements, enhancements, bug reports, or
                  other feedback you provide (collectively, "Feedback") shall
                  become the exclusive property of Hermes Terminal.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  18.2. <strong className="text-white">No Compensation:</strong>{" "}
                  You acknowledge that you will not receive compensation, credit,
                  or ownership rights for any Feedback.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  18.3. <strong className="text-white">IP Transfer:</strong>{" "}
                  You hereby irrevocably assign to Hermes Terminal all rights,
                  title, and interest in and to any Feedback, including all
                  intellectual property rights therein.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  18.4. <strong className="text-white">License to Implement:</strong>{" "}
                  Hermes Terminal may use, implement, modify, publish, or
                  commercialize Feedback without restriction and without any
                  obligation to you.
                </p>
              </div>
            </section>

            {/* Section 19: Additional Beta Access Restrictions */}
            <section className="mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                19. Additional Beta Access Restrictions
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  19.1. <strong className="text-white">Confidential Nature of Beta:</strong>{" "}
                  All aspects of the Beta version of the Service, including its
                  existence, are considered Confidential Information unless
                  publicly announced by Hermes Terminal.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  19.2. <strong className="text-white">Early-Access Limitations:</strong>{" "}
                  Your access to the Beta does not grant you any rights to
                  continued access, future releases, or commercial versions of
                  the Service.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  19.3. <strong className="text-white">Termination of Beta Access:</strong>{" "}
                  Hermes Terminal may revoke your Beta access at any time for
                  any reason, including suspected confidentiality breaches.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  19.4. <strong className="text-white">Return or Destruction:</strong>{" "}
                  Upon request, you agree to destroy any materials, notes,
                  screenshots, or data related to the Beta.
                </p>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  19.5. <strong className="text-white">Survival:</strong>{" "}
                  The confidentiality and non-competition obligations in
                  Sections 16–19 shall survive termination of your account and
                  remain in effect indefinitely.
                </p>
              </div>
            </section>

            {/* Final Acknowledgment */}
            <section className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-gray-700">
              <h3 className="text-lg sm:text-xl font-bold text-white mb-4 text-center">
                Final Acknowledgment
              </h3>
              <p className="text-gray-300 text-sm sm:text-base leading-relaxed text-center font-medium mb-6">
                BY ACCESSING OR USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE
                READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS AND
                CONDITIONS.
              </p>


            </section>
          </div>
        </div>

        {/* Footer - Sticky at bottom with action buttons */}
        <div className="sticky bottom-0 bg-gray-800/95 backdrop-blur-xl border-t border-gray-700 px-4 sm:px-6 py-3 sm:py-4 rounded-b-xl sm:rounded-b-2xl">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button
              onClick={isBlocking ? onReject : onClose}
              disabled={isLoading}
              className={`flex-1 py-2.5 sm:py-3 px-4 sm:px-6 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-all text-sm sm:text-base ${
                isLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}>
              {isBlocking ? "Reject & Logout" : "Close"}
            </button>
            {onAccept && (
              <button
                onClick={handleAccept}
                disabled={isLoading}
                className={`flex-1 py-2.5 sm:py-3 px-4 sm:px-6 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-lg font-medium transition-all text-sm sm:text-base flex items-center justify-center gap-2 ${
                  isLoading ? "opacity-70 cursor-not-allowed" : ""
                }`}>
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  "I Accept These Terms"
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
